import { useEffect, useRef, useState } from 'react';
import BaseApi from '../../../services/BaseApi';
import Modal from '../../Modal';
import EvidenceUpload from './EvidenceUpload';
import { evidenceFields, submissionId } from './viabilityDefinitions';

export const canCorrect = (pending: any) => pending.statusCode === 1 || pending.statusCode === 4;
const categoryLabel = (pending: any) => pending.categoryLabel || evidenceFields.find(([key]) => key === pending.category)?.[1] || pending.category;
const statusLabels: Record<number, string> = { 1: 'Aguardando correção', 2: 'Aguardando análise', 3: 'Aprovado', 4: 'Recusado' };

export function ViabilityPendingPanel({ study }: { study: any }) {
  const actionable = (study?.pendings || []).filter(canCorrect);
  if (!actionable.length) return null;
  return <div className="border rounded p-3 my-2">
    <strong>Pendências de viabilidade</strong>
    {actionable.map((pending: any) => <div key={pending.id} className="border-top mt-2 pt-2">
      <strong>{categoryLabel(pending)}</strong>
      <span className={`d-block ${pending.statusCode === 3 ? 'text-success' : pending.statusCode === 4 ? 'text-danger' : 'text-muted'}`}>
        {statusLabels[pending.statusCode] || 'Status indisponível'}
      </span>
      <p className="mb-1">{pending.description}</p>
      {pending.statusCode === 4 && <p className="text-danger mb-1">Motivo da recusa: {pending.descriptionRejected || pending.description}</p>}
      {pending.response && <small className="d-block">Última resposta: {pending.response}</small>}
    </div>)}
  </div>;
}

export function ViabilityCorrectionForm({ proposalId, initialStudy, onUpdated }: { proposalId: string; initialStudy: any; onUpdated: (study: any) => void }) {
  const [study, setStudy] = useState(initialStudy);
  const [files, setFiles] = useState<Record<string, File>>({});
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [id, setId] = useState(submissionId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const pending = (study?.pendings || []).filter(canCorrect);
  const categories: string[] = Array.from(new Set<string>(pending.map((p: any) => p.category)));
  function changed() { setId(submissionId()); setSuccess(''); }
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError('');
    const selected = pending.filter((p: any) => files[p.category]);
    if (!selected.length || selected.some((p: any) => !responses[p.id]?.trim())) {
      setError('Selecione o arquivo corrigido e informe a resposta para cada pendência selecionada.'); return;
    }
    const data = new FormData();
    data.append('submissionId', id); data.append('viabilityStudyId', study.viabilityStudyId);
    data.append('pendingResponses', JSON.stringify(selected.map((p: any) => ({ pendingId: p.id, response: responses[p.id].trim() }))));
    Object.entries(files).forEach(([category, file]) => data.append(category, file));
    setBusy(true);
    try {
      const saved: any = await BaseApi.post(`api/proposal/${proposalId}/viability`, data);
      setStudy(saved); onUpdated(saved); setFiles({}); setResponses({}); setId(submissionId());
      setSuccess('Correção recebida. Aguardando análise do Salesforce.');
    } catch (e) { setError(Modal.getErrorMessageText(e)); }
    finally { setBusy(false); }
  }
  return <form onSubmit={submit}>
    <ViabilityPendingPanel study={study} />
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {success && <div role="status" className="alert alert-success">{success}</div>}
    {categories.length > 0 && <fieldset disabled={busy}>
      <p>Envie somente os arquivos solicitados. A correção será analisada antes da aprovação.</p>
      {categories.map(category => {
        const definition = evidenceFields.find(([key]) => key === category);
        if (!definition) return <div role="alert" key={category}>Categoria de correção não reconhecida: {category}. Entre em contato com o suporte.</div>;
        const existing = study.evidences?.find((e: any) => e.category === category);
        return <div key={category}>
          <EvidenceUpload category={category} label={definition[1]} help={definition[2]} existing={existing} file={files[category]} required={false}
            onError={setError} onChange={file => {
              setFiles(previous => { const next = { ...previous }; if (file) next[category] = file; else delete next[category]; return next; }); changed();
            }} />
          {pending.filter((p: any) => p.category === category).map((p: any) => <label className="d-block mb-3" key={p.id}>
            Resposta à pendência: {p.description}
            <textarea className="form-control" maxLength={4000} required={!!files[category]} value={responses[p.id] || ''}
              onChange={e => { setResponses(previous => ({ ...previous, [p.id]: e.target.value })); changed(); }} />
          </label>)}
        </div>;
      })}
      <button type="submit" className="btn btn-primary" disabled={busy || Object.keys(files).length === 0}>
        {busy ? 'Enviando correção...' : 'Enviar correção'}
      </button>
    </fieldset>}
  </form>;
}

export default function ViabilityPendingAction({ proposalId, onOpenUploads }: { proposalId: string; onOpenUploads?: () => void }) {
  const [study, setStudy] = useState<any>();
  const [error, setError] = useState('');
  const refresh = useRef<() => void>(() => {});
  const revision = useRef(0);
  useEffect(() => {
    let active = true;
    let controller: AbortController | undefined;
    async function load() {
      controller?.abort(); controller = new AbortController();
      const current = ++revision.current;
      try {
        const value: any = await BaseApi.get(`api/proposal/${proposalId}/viability`, { signal: controller.signal });
        if (active && current === revision.current) { setStudy(value); setError(''); }
      } catch (e) {
        if (active && current === revision.current && !controller.signal.aborted) setError(Modal.getErrorMessageText(e));
      }
    }
    refresh.current = load;
    load();
    const focus = () => { if (document.visibilityState === 'visible') load(); };
    const timer = window.setInterval(focus, 60000);
    window.addEventListener('focus', focus);
    return () => { active = false; controller?.abort(); window.clearInterval(timer); window.removeEventListener('focus', focus); };
  }, [proposalId]);
  const pendings = study?.pendings || [];
  const needsCorrection = pendings.some(canCorrect);
  const awaitingValidation = pendings.some((p: any) => p.statusCode === 2) ||
    (study?.collectionStatus === 'ENVIADA' && pendings.length === 0);
  const firstSubmission = study?.collectionStatus !== 'ENVIADA' && study?.canEdit;
  const showUploads = !!study && !error && !needsCorrection && (firstSubmission || awaitingValidation);
  return <>
    {showUploads && onOpenUploads && <button type="button" className="btn btn-primary mb-2 me-2" onClick={onOpenUploads}>Enviar imagens e vídeos do local</button>}
    {error && <div role="alert" className="alert alert-warning">Não foi possível atualizar as pendências de viabilidade. <button type="button" className="btn btn-link" onClick={() => refresh.current()}>Consultar novamente</button></div>}
    <ViabilityPendingPanel study={study} />
    {!error && study?.pendings?.some(canCorrect) && <button type="button" className="btn btn-primary mb-2" onClick={() => {
      Modal.show({ title: study.pendings.some(canCorrect) ? 'Corrigir pendências de viabilidade' : 'Consultar pendências de viabilidade', showConfirm: false, showCancel: true, modalProps: { size: 'lg' },
        body: <ViabilityCorrectionForm proposalId={proposalId} initialStudy={study} onUpdated={value => { revision.current++; setStudy(value); }} /> });
    }}>{study.pendings.some(canCorrect) ? 'Corrigir imagens e vídeos pendentes' : 'Consultar imagens e vídeos enviados'}</button>}
  </>;
}
